import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { getMyPblLawyerApplication } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import { Button } from '@/components/UI/Button';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { useAppSelector } from '@/redux/store.hook';
import { pblVettingStatusLabels } from '@/data/pbl';
import { fakePblCurrentLawyerApplication } from '@/pages/Pbl/fakeData';
import type { PblLawyerProfile, PblVettingStatus } from '@/types/pbl';
import { getPblLawyerVettingStatus } from '@/utils/pblRole';
import useCurrentLang from '@/hooks/useCurrentLang';
import useResize from '@/hooks/useResize';
import { useTranslation } from 'react-i18next';

const StatusCard = styled.div`
  box-sizing: border-box;
  width: min(100%, 760px);
  min-width: 0;
  min-height: 300px;
  padding: 38px 48px;
  background: ${(p) => p.theme.colors.light};
  border-radius: ${(p) => p.theme.radii.lg};

  @media (max-width: 768px) {
    padding: 30px 24px;
  }

  @media (max-width: 639px) {
    width: 100%;
    min-height: 0;
    padding: 24px 12px 28px;
    border-radius: 12px;
  }
`;

const Title = styled.h1`
  margin: 0 0 28px;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.md};
  line-height: 1.3;
  overflow-wrap: anywhere;

  @media (max-width: 639px) {
    margin-bottom: 20px;
    font-size: 20px;
  }
`;

const BodyText = styled.p`
  margin: 0 0 20px;
  line-height: 1.55;
  color: ${(p) => p.theme.colors.gray500};
  overflow-wrap: anywhere;
`;

const StatusText = styled.span<{ status: PblVettingStatus }>`
  color: ${(p) =>
    p.status === 'approved'
      ? p.theme.colors.secondary500
      : p.status === 'rejected'
      ? p.theme.colors.danger
      : p.status === 'inactive' || p.status === 'withdrawn'
      ? p.theme.colors.gray300
      : p.theme.colors.red};
`;

const ActionRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};
  gap: 14px;
  margin-top: 48px;

  @media (max-width: 639px) {
    margin-top: 28px;

    button {
      width: 100%;
    }
  }
`;

const validStatuses: PblVettingStatus[] = ['pending_review', 'approved', 'resubmit_required', 'rejected', 'inactive', 'withdrawn'];

type LawyerApplicationStatusContent = Record<
  PblVettingStatus,
  {
    title: string;
    body: string;
    secondary?: string;
  }
>;

const getStatusFromUser = (userData: any): PblVettingStatus | undefined => {
  return getPblLawyerVettingStatus(userData);
};

const PblLawyerApplicationStatus = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentLang = useCurrentLang();
  const { isMobile } = useResize();
  const [searchParams] = useSearchParams();
  const [application, setApplication] = useState<PblLawyerProfile | null>(null);
  const { userData } = useAppSelector(getAuthSelector);
  const statusContent = useMemo<LawyerApplicationStatusContent>(
    () => ({
      pending_review: {
        title: t('pbl.pagesHardcoded.lawyerApplicationStatus.applicationPendingReview'),
        body: 'Your FreelegalLink volunteer lawyer application is being reviewed by LegCo.',
        secondary: t('pbl.pagesHardcoded.lawyerApplicationStatus.youWillBeNotifiedAfterTheVettingResult'),
      },
      approved: {
        title: t('pbl.pagesHardcoded.lawyerApplicationStatus.applicationApproved'),
        body: 'Your volunteer lawyer application has been approved.',
        secondary: t('pbl.pagesHardcoded.lawyerApplicationStatus.youAreNowEligibleToReceiveFreelegallinkEnquiry'),
      },
      resubmit_required: {
        title: t('pbl.pagesHardcoded.lawyerApplicationStatus.applicationUpdateRequired'),
        body: 'Your FreelegalLink volunteer lawyer application has not been approved.',
      },
      rejected: {
        title: t('pbl.pagesHardcoded.lawyerApplicationStatus.applicationRejected'),
        body: 'Your FreelegalLink volunteer lawyer application has not been approved.',
      },
      inactive: {
        title: t('pbl.pagesHardcoded.lawyerApplicationStatus.applicationInactive'),
        body: 'Your FreelegalLink volunteer lawyer application is currently inactive.',
      },
      withdrawn: {
        title: 'FreeLegalLink membership withdrawn',
        body: 'This account no longer has access to FreeLegalLink. You may submit a new volunteer registration using the same verified email address.',
      },
    }),
    [t]
  );

  useEffect(() => {
    let isMounted = true;

    const loadApplication = async () => {
      try {
        const response = await getMyPblLawyerApplication();

        if (!isMounted) return;
        if (response.data.success === false) return;

        setApplication(response.data.data);
      } catch {
        if (isMounted) setApplication(null);
      }
    };

    loadApplication();

    return () => {
      isMounted = false;
    };
  }, []);

  const status = useMemo<PblVettingStatus>(() => {
    const queryStatus = searchParams.get('status');
    const fromQuery = validStatuses.find((item) => item === queryStatus);
    const fromApplication = application?.vetting_status || application?.status;
    const fromUser = getStatusFromUser(userData);

    return fromQuery || fromApplication || fromUser || fakePblCurrentLawyerApplication.status;
  }, [application, searchParams, userData]);

  const content = statusContent[status];
  const showResubmitActions = status === 'resubmit_required';
  const returnReason =
    application?.return_reason_other || application?.return_reason || fakePblCurrentLawyerApplication.return_reason;
  const reviewerComments = application?.reviewer_comments || fakePblCurrentLawyerApplication.reviewer_comments;

  const goEditApplication = () => {
    navigate(`/${currentLang}/volunteerRegistration?step=1&mode=resubmit`);
  };

  return (
    <PageContainer title="-">
      <StatusCard>
        <Title>{content.title}</Title>
        <BodyText>{content.body}</BodyText>
        {content.secondary && <BodyText>{content.secondary}</BodyText>}
        <BodyText>
          {t('pbl.pagesHardcoded.lawyerApplicationStatus.status')}<StatusText status={status}>{pblVettingStatusLabels[status]}</StatusText>
        </BodyText>

        {showResubmitActions && (
          <>
            <BodyText>
              {t('pbl.pagesHardcoded.lawyerApplicationStatus.reason')}<br />
              {returnReason}
            </BodyText>
            <BodyText>
              {t('pbl.pagesHardcoded.lawyerApplicationStatus.comments')}<br />
              {reviewerComments}
            </BodyText>
          </>
        )}

        <ActionRow isMobile={isMobile}>
          {showResubmitActions && (
            <Button size="lg" variant="primary" rounded={false} onClick={goEditApplication}>
              {t('pbl.pagesHardcoded.lawyerApplicationStatus.editApplication')}</Button>
          )}
        </ActionRow>
      </StatusCard>
    </PageContainer>
  );
};

export default PblLawyerApplicationStatus;
